import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import { useGetPostByIdQuery } from '@/redux/post/post.api.slice';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';
import { IBitPublic } from '@/redux/bit/bit.d';
import { IPostDetailResponse } from '@/redux/post/post.d';
import { STEP_LABELS } from './stepLabels';
import {
  addFiles,
  resetAll,
  setBasics,
  setCamExtracted,
  setCamExtractedFileId,
  setCamParsing,
  setExtractedData,
  setFileToolpaths,
  setFileExtracted,
  setJobDetails,
  setOperations,
  setParsedReady,
  setParsing,
  setPostId,
  setStep,
  setOrderConfirmed,
  setThumbnailFileId,
  setVideoLinks,
  selectV2,
  setMode,
  setGroupId,
  setChildIds,
  setWorkpieceIndex,
  setGroupCover,
  setGroupDetails,
  setMultiStage,
  clearEditingChild,
} from './v2Upload.slice';
import { useGetProjectGroupByIdQuery } from '@/redux/projectGroup/projectGroup.api.slice';
import { clearSelectedProjects } from '@/redux/projectGroup/projectGroup.slice';
import { V2File } from './v2Upload.d';
import { isCamFile } from '@/workers/camFiles';
import {
  clampResumeStep,
  reparseRestoredCam,
  reparseRestoredGcode,
  restoreBasics,
  restoreFiles,
  restoreGroupCoverUrl,
  restoreGroupDetails,
  restoreJobDetails,
  restoreOperations,
  photosNeedingReupload,
  restoreThumbnailFileId,
  restoreVideoLinks,
  resumeStageFor,
} from './utils/restorePost';

type Dispatch = ReturnType<typeof useDispatch>;

/** Files first: the operations map the post's attachment ids back onto the file
 * ids handed out here, and the thumbnail is matched against the restored
 * images. Returns them so the caller can re-read their G-code. */
const FILES_STEP = STEP_LABELS.indexOf('Add files');
const BASICS_STEP = STEP_LABELS.indexOf('Basics');

function hydrate(
  post: IPostDetailResponse,
  bits: IBitPublic[] | undefined,
  dispatch: Dispatch
): { files: V2File[]; dropped: string[] } {
  const attachments = post.attachments ?? [];
  const dropped = photosNeedingReupload(attachments);
  const files = restoreFiles(attachments).filter(
    (f) => !f.attachmentId || !dropped.includes(f.attachmentId)
  );

  // The group route carries no post id, so without this the next sync creates a
  // second draft instead of writing to the workpiece just restored.
  dispatch(setPostId(post._id));
  dispatch(setBasics(restoreBasics(post)));
  dispatch(setJobDetails(restoreJobDetails(post)));
  dispatch(addFiles(files));
  dispatch(setThumbnailFileId(restoreThumbnailFileId(attachments, files)));
  dispatch(setVideoLinks(restoreVideoLinks(attachments)));
  dispatch(setOperations(restoreOperations(post.modelGroups, files, bits)));
  // A draft that already walked past Add files had its carve order signed off
  // then; the flag is local state, so without this the rail re-gates it.
  const reached = clampResumeStep(post.currentPage);
  dispatch(setOrderConfirmed(reached > FILES_STEP));
  // Replayed before Basics so the rail keeps every step already reached.
  dispatch(setStep(reached));
  dispatch(setStep(BASICS_STEP));

  return { files, dropped };
}

/** Clears the photos a post could never turn into a thumbnail. The Add files
 * gate already refuses to move on without one, so removing them is what asks
 * the user for a photo they still have the bytes for. */
function askForPhotoAgain(
  dropped: string[],
  deleteAttachment: (arg: { attachmentId: string }) => {
    unwrap: () => Promise<unknown>;
  }
) {
  if (dropped.length === 0) return;
  dropped.forEach((attachmentId) => {
    deleteAttachment({ attachmentId })
      .unwrap()
      .catch(() => {});
  });
  toast.warning('Add your project photo again', {
    description:
      'It was not saved as this project’s thumbnail, so it has been removed.',
  });
}

/** The dropped design file, re-read so a resumed draft opens its read-out and
 * seeds job details exactly as a fresh drop does. */
function reparseCamInBackground(files: V2File[], dispatch: Dispatch) {
  if (!files.some((f) => isCamFile(f.name))) return;
  dispatch(setCamParsing(true));
  reparseRestoredCam(files)
    .then((cam) => {
      if (!cam) return;
      dispatch(setCamExtracted(cam.data));
      dispatch(setCamExtractedFileId(cam.fileId));
    })
    .finally(() => dispatch(setCamParsing(false)));
}

/** Toolpaths and job data are recovered by re-reading the G-code from S3, off
 * the render path — the wizard is usable while it runs. `parsedReady` is set
 * whatever the outcome, or Sign off would sit on "still reading" forever. */
function reparseInBackground(files: V2File[], dispatch: Dispatch) {
  // The CAM design is re-read too, and on its own schedule: a draft can hold
  // one without any G-code, and its data is not persisted anywhere else.
  reparseCamInBackground(files, dispatch);

  if (!files.some((f) => f.kind === 'gcode')) return;
  dispatch(setParsing(true));
  reparseRestoredGcode(files)
    .then(({ toolpaths, extractedByFile, extractedData }) => {
      dispatch(setFileToolpaths(toolpaths));
      dispatch(setFileExtracted(extractedByFile));
      if (extractedData) dispatch(setExtractedData(extractedData));
    })
    .finally(() => {
      dispatch(setParsedReady(true));
      dispatch(setParsing(false));
    });
}

/**
 * Boots the wizard: a fresh start on /project/v2/upload, or a resume on
 * /project/v2/edit/:postId (where `createDraft` navigates once a draft exists).
 * A resume re-reads the draft from the server and puts back everything the user
 * had — details, step, uploaded files with their previews and controllers, and
 * the bit groups.
 *
 * Only files dropped but never carried past Add files are lost: those were
 * never uploaded, and a `File` handle dies with the page. Re-dropping one is
 * deduplicated server-side by its content hash.
 */
// fallow-ignore-next-line complexity
export default function useDraftResume(): void {
  const dispatch = useDispatch();
  const { postId: routePostId, groupId: routeGroupId } = useParams();
  const { mode, editingChildId } = useSelector(selectV2);

  // Read once, at mount. `createDraft` navigates onto /project/v2/edit/:postId
  // mid-flow, and reacting to that would reset the wizard out from under the
  // user — and re-hydrating the draft they just created would bounce them back
  // to Basics. Only a draft the wizard was *opened* on is ever restored.
  const [initialPostId] = useState(() => routePostId ?? null);
  const [initialGroupId] = useState(() => routeGroupId ?? null);
  const booted = useRef(false);
  const hydrated = useRef(false);
  const groupHydrated = useRef(false);

  const { data: group } = useGetProjectGroupByIdQuery(
    { id: initialGroupId ?? '' },
    { skip: !initialGroupId }
  );

  const groupStage = group ? resumeStageFor(group.visibility) : undefined;

  // Last child, not first: resume reopens the workpiece in progress. Editing a
  // submitted group opens on review instead, so it reopens nothing.
  const resumeChildId =
    groupStage === 'workpiece' && group?.projects?.length
      ? (group.projects[group.projects.length - 1]._id ?? null)
      : null;

  const { data: post } = useGetPostByIdQuery(
    initialPostId ?? resumeChildId ?? '',
    { skip: !initialPostId && !resumeChildId }
  );
  // The picker renders the selected bit out of this same catalog; waiting for
  // it means a restored operation shows its bit's name instead of a blank.
  const { data: bits, isLoading: bitsLoading } = useGetActiveBitsQuery();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    dispatch(resetAll());
    // Lives in a slice that outlives the route, so a set abandoned elsewhere
    // would arrive here pre-ticked.
    dispatch(clearSelectedProjects());
    if (initialPostId) dispatch(setPostId(initialPostId));
    if (initialGroupId) {
      dispatch(setMode('multi'));
      dispatch(setGroupId(initialGroupId));
    }
  }, [dispatch, initialPostId, initialGroupId]);

  // Restores the set: its ids, its details, and which workpiece is in progress.
  // fallow-ignore-next-line complexity
  useEffect(() => {
    if (groupHydrated.current || !group) return;
    groupHydrated.current = true;
    const children = group.projects ?? [];
    dispatch(setChildIds(children.map((child) => child._id)));
    dispatch(setWorkpieceIndex(Math.max(children.length, 1)));
    dispatch(setGroupDetails(restoreGroupDetails(group)));
    const cover = restoreGroupCoverUrl(group);
    if (cover) dispatch(setGroupCover({ preview: cover }));
    if (groupStage === 'review') dispatch(setMultiStage('review'));
  }, [group, groupStage, dispatch]);

  // Every sync invalidates the Post tag and so refetches this query. Hydrating
  // a second time would throw away whatever the user did after the first.
  // The old one-screen group flow has no draft to hydrate; the inline flow does.
  const skipHydrate = mode === 'multi' && !initialGroupId;
  // fallow-ignore-next-line complexity
  useEffect(() => {
    if (hydrated.current || !post || bitsLoading || skipHydrate) return;
    hydrated.current = true;
    const { files, dropped } = hydrate(post, bits, dispatch);
    reparseInBackground(files, dispatch);
    askForPhotoAgain(dropped, deleteAttachment);
  }, [post, bits, bitsLoading, skipHydrate, dispatch, deleteAttachment]);

  const { data: editingChild } = useGetPostByIdQuery(editingChildId ?? '', {
    skip: !editingChildId,
  });

  // fallow-ignore-next-line complexity
  useEffect(() => {
    if (!editingChildId || !editingChild || bitsLoading) return;
    if (editingChild._id !== editingChildId) return;
    dispatch(clearEditingChild());
    const { files, dropped } = hydrate(editingChild, bits, dispatch);
    reparseInBackground(files, dispatch);
    askForPhotoAgain(dropped, deleteAttachment);
  }, [
    editingChildId,
    editingChild,
    bits,
    bitsLoading,
    dispatch,
    deleteAttachment,
  ]);
}
